import { z } from "zod";

/**
 * Pagination contract shared by every list endpoint (decision: offset/limit, not
 * cursor). One request shape in, one {@link Paginated} result shape out, so the
 * client `DataTable` server-mode and the data-layer adapters never drift. The
 * in-memory adapter slices with {@link paginate}; the PostgreSQL adapter (Phase
 * 7) maps the same fields to `LIMIT`/`OFFSET` + a `COUNT(*)`.
 *
 * `page` is 1-based (matches the «Seite X von N» footer); `pageSize` is capped so
 * a caller can never request an unbounded page — the whole point of the rollout.
 */

/** Default rows per page when a caller omits `pageSize`. */
export const DEFAULT_PAGE_SIZE = 20;

/** Hard ceiling on `pageSize` so no request degenerates into a fetch-everything. */
export const MAX_PAGE_SIZE = 100;

/** Sort direction for a paginated query. */
export const SortDirectionSchema = z.enum(["asc", "desc"]);
export type SortDirection = z.infer<typeof SortDirectionSchema>;

/**
 * Boundary schema for a page request. Entity-specific filters (e.g. `category`,
 * `sender`, `status`) extend this in the entity's own input-schema file — keep
 * this base generic.
 */
export const PageRequestSchema = z.object({
    /** 1-based page index. */
    page: z.number().int().min(1).default(1),
    /** Rows per page, capped at {@link MAX_PAGE_SIZE}. */
    pageSize: z.number().int().min(1).max(MAX_PAGE_SIZE).default(DEFAULT_PAGE_SIZE),
    /** Column id to sort by; the adapter decides which ids are sortable. */
    sortBy: z.string().min(1).optional(),
    /** Sort direction; ignored unless `sortBy` is set. */
    sortDir: SortDirectionSchema.optional(),
    /** Free-text search; the adapter decides which fields it matches. */
    search: z.string().trim().optional(),
});
export type PageRequest = z.infer<typeof PageRequestSchema>;

/** The single return shape for every paginated list endpoint. */
export interface Paginated<T> {
    /** The rows for the requested page. */
    items: T[];
    /** Total rows across all pages (post-filter), for «Seite X von N». */
    total: number;
    /** Echo of the resolved 1-based page. */
    page: number;
    /** Echo of the resolved page size. */
    pageSize: number;
}

/** The total number of pages for a `total`/`pageSize` pair (>= 1 when empty). */
export const pageCount = (total: number, pageSize: number): number =>
    pageSize > 0 ? Math.max(1, Math.ceil(total / pageSize)) : 1;

/**
 * Slice an already-materialised array into a {@link Paginated} page. The shared
 * in-memory adapter primitive: callers filter and sort `items` themselves (the
 * fields differ per entity), then hand the prepared array here for the offset
 * maths and `total`. The page is clamped into range so an out-of-bounds `page`
 * yields an empty slice with the real `total`, never a negative offset.
 *
 * @typeParam T - the row shape.
 */
export const paginate = <T>(
    items: readonly T[],
    request: Pick<PageRequest, "page" | "pageSize">
): Paginated<T> => {
    const pageSize = request.pageSize;
    const total = items.length;
    const lastPage = pageCount(total, pageSize);
    const page = Math.min(Math.max(request.page, 1), lastPage);
    const start = (page - 1) * pageSize;
    return {
        items: items.slice(start, start + pageSize),
        total,
        page,
        pageSize,
    };
};
